import { z } from 'zod';

import {
  recommendShipmentRangeStatusValues,
  recommendShipmentRangeUnavailableReasonValues,
} from './recommend-shipment-range.dto.js';
import type {
  GetShipmentInventorySummaryRequest,
  GetShipmentInventorySummaryResponse,
  ShipmentInventorySummaryRow,
} from './get-shipment-inventory-summary.dto.js';
import { IsoDateTimeSchema, RcScopeTypeSchema } from './shipments-types.js';

const BusinessDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const NonNegativeNumberSchema = z.number().min(0);
const NonNegativeIntSchema = z.number().int().min(0);

export const GetShipmentInventorySummaryRequestSchema: z.ZodType<GetShipmentInventorySummaryRequest> =
  z
    .object({
      companyId: z.uuid(),
      storeId: z.uuid().optional(),
      locationId: z.uuid().optional(),
      stationId: z.uuid(),
    })
    .strict();

const ShipmentInventorySummaryRowSchema: z.ZodType<ShipmentInventorySummaryRow> = z
  .object({
    materialId: z.uuid(),
    materialCode: z.string().trim().min(1).nullable(),
    materialDisplayName: z.string().trim().min(1),
    generatedWeightLb: NonNegativeNumberSchema,
    departedWeightLb: NonNegativeNumberSchema,
    reservedWeightLb: NonNegativeNumberSchema,
    availableUnshippedWeightLb: NonNegativeNumberSchema,
    unshippedTotalWeightLb: NonNegativeNumberSchema,
    generatedLineCount: NonNegativeIntSchema,
    departedLineCount: NonNegativeIntSchema,
    reservedLineCount: NonNegativeIntSchema,
    availableLineCount: NonNegativeIntSchema,
    oldestAvailableOccurredAt: IsoDateTimeSchema.nullable(),
    recommendationStatus: z.enum(recommendShipmentRangeStatusValues),
    recommendationUnavailableReason: z
      .enum(recommendShipmentRangeUnavailableReasonValues)
      .nullable(),
    recommendedStartAt: IsoDateTimeSchema.nullable(),
    recommendedEndAt: IsoDateTimeSchema.nullable(),
    previousCutoffAt: IsoDateTimeSchema.nullable(),
    lastClosedBusinessDate: BusinessDateSchema.nullable(),
    lastReportedRangeEndAt: IsoDateTimeSchema.nullable(),
  })
  .strict();

export const GetShipmentInventorySummaryResponseSchema: z.ZodType<GetShipmentInventorySummaryResponse> =
  z
    .object({
      generatedAt: IsoDateTimeSchema,
      companyId: z.uuid(),
      storeId: z.uuid(),
      locationId: z.uuid(),
      stationId: z.uuid(),
      rcScopeType: RcScopeTypeSchema,
      rcScopeId: z.uuid(),
      timeZone: z.string().trim().min(1).nullable(),
      rows: z.array(ShipmentInventorySummaryRowSchema),
    })
    .strict();
